import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import VerticalCarousel from '../components/VerticalCarousel';
import Odometer from 'react-odometerjs';
import 'odometer/themes/odometer-theme-default.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import useAos from '../hooks/useAos';
import { PageMeta } from '../seo/PageMeta';
import LocalBusinessSchema from '../seo/LocalBusinessSchema';

import batimentImg from '../images/batiment.webp';
import palaisJusticeImage2 from '../images/realisation/Palais de justice/image2.png';
import centreWemotaciImage8 from "../images/realisation/Centre Manonikotiwam/image8.png";
import postePolice2Image1 from '../images/realisation/poste de police/image.png';

import pictoBatiment from '../images/pictogramme/BATIMENT_HD.png';
import pictoIndustriel from '../images/pictogramme/INDUSTRIEL_HD.png';
import pictoMunicipal from '../images/pictogramme/MUNICIPAL_HD.png';
import pictoGestionDeProjets from '../images/pictogramme/GESTION_DE_PROJET_HD.png';
import pictoAutres from '../images/pictogramme/AUTRES_HD.png';

const REALISATIONS_IMAGES = [centreWemotaciImage8, palaisJusticeImage2, postePolice2Image1];

const SERVICE_KEYS = [
    { key: 'batiment', href: '/services#batiment', delay: 0, image: pictoBatiment },
    { key: 'industriel', href: '/services#industriel', delay: 100, image: pictoIndustriel },
    { key: 'municipal', href: '/services#municipal', delay: 200, image: pictoMunicipal },
    { key: 'gestion-de-projets', href: '/services#gestion-de-projets', delay: 300, image: pictoGestionDeProjets },
    { key: 'autres', href: '/services#autres', delay: 400, image: pictoAutres },
];

function Hero() {
    const navigate = useNavigate();
    const { t } = useTranslation(['pages', 'common']);
    const statsRef = useRef(null);
    const [isStatsVisible, setIsStatsVisible] = useState(false);
    const [projets, setProjets] = useState(0);
    const [clients, setClients] = useState(0);

    useEffect(() => {
        const element = statsRef.current;
        if (!element) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
                    setIsStatsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: [0, 0.4, 0.6] }
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isStatsVisible) return undefined;

        const frame = requestAnimationFrame(() => {
            setProjets(1500);
            setClients(40);
        });

        return () => cancelAnimationFrame(frame);
    }, [isStatsVisible]);

    return (
        <section className="flex w-full overflow-hidden hero-section" data-navbar-theme="light">
            <div className="batimentImg hero-section__image w-50 shrink-0">
                <img
                    src={batimentImg}
                    alt={t('pages:home.heroImageAlt')}
                    className="hero-section__photo w-full h-full object-cover"
                    data-aos="fade-right"
                    data-aos-duration="700"
                    fetchPriority='high'
                />
            </div>
            <div className="w-50 flex flex-column px-32 hero-section__content" style={{ minHeight: 0 }}>
                <div className="shrink-0 hero-section__intro">
                    <p className="text-title text-bold fustat" data-aos="fade-up" data-aos-delay="150">{t('pages:home.heroTitle')}</p>
                    <div data-aos="fade" data-aos-delay="300">
                        <button type="button" onClick={() => navigate('/nousjoindre')} className="btn-blue w-full mb-32 flex items-center justify-center gap-16"><span>{t('common:buttons.contactUs')}</span> <FontAwesomeIcon icon={faAngleRight} /></button>
                    </div>
                    <div data-aos="fade" data-aos-delay="900">
                        <hr className="mt-32 hero-section__divider" style={{ border: '0.2px solid var(--graylight)', opacity: 0.2 }}
 />
                    </div>
                </div>
                <div
                    className={`flex justify-around flex-1 items-center hero-section__stats${isStatsVisible ? ' hero-section__stats--visible' : ''}`}
                    ref={statsRef}
                    style={{ minHeight: 0 }}
                >
                    <div>
                        <div className="text-title2 text-normal outfit m-0 p-0">
                            {isStatsVisible ? <Odometer value={projets} format="(,ddd)" /> : '0'} +
                        </div>
                        <p className="text-content m-0 p-0 outfit opacity-50">{t('pages:home.projectsCompleted')}</p>
                    </div>
                    <div className="border-left shrink-0" style={{ height: 'min(150px, 100%)', alignSelf: 'center' }} aria-hidden="true" />
                    <div>
                        <div className="text-title2 text-normal outfit m-0 p-0">
                            {isStatsVisible ? <Odometer value={clients} format="(,ddd)" /> : '0'} +
                        </div>
                        <p className="text-content m-0 p-0 outfit opacity-50">{t('pages:home.clientsIn15Years')}</p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Services() {
    const { t } = useTranslation(['pages', 'common']);

    return (
        <section className="w-full home-services" data-navbar-theme="light">
            <div className="flex home-services__heading justify-center text-center">
                <p className="text-title text-semibold fustat text-center mb-0" style={{ maxWidth: '1000px'}} data-aos="fade-up">{t('pages:home.servicesTitle')}</p>
            </div>
            <div className="flex home-services-grid items-center">
                {SERVICE_KEYS.map(({ key, href, delay, image }) => {
                    const label = t(`pages:services.items.${key}.title`);
                    return (
                        <Link
                            key={href}
                            to={href}
                            className="image-inline-shadow relative text-deco-none border-graylight"
                            data-aos="fade-up"
                            data-aos-delay={delay}
                        >
                            <img src={image} alt={label} />
                            <p className="image-label t-white fustat text-title3">{label}</p>
                        </Link>
                    );
                })}
            </div>

            <div className="flex justify-center home-services__cta" data-aos="fade-up">
                <a href="/services" className="btn-link text-center">{t('common:buttons.seeAllServices')}</a>
            </div>

            <div className="home-quebec">
                <div className="flex items-center gap-32 w-full home-quebec__divider-row">
                    <hr className="flex-1 home-quebec__line" data-aos="fade-right" data-aos-delay="500" />
                    <p className="text-subtitle outfit m-0 t-graylight text-regular home-quebec__label" data-aos="fade-up">
                        {t('pages:home.quebecLabelBefore')} <span className="t-blueqc text-regular">{t('common:labels.quebec')}</span>
                    </p>
                    <hr className="flex-1 home-quebec__line" data-aos="fade-left" data-aos-delay="500" />
                </div>
                <div className="flex justify-center text-center">
                    <p className="text-title3 outfit home-quebec__text" style={{ maxWidth: '1000px'}} data-aos="fade-up" data-aos-delay="150">{t('pages:home.quebecText')}</p>
                </div>
                <div data-aos="fade" data-aos-delay="300">
                    <hr className="home-quebec__bottom-line" style={{ border: '0.2px solid var(--graylight)', opacity: 0.2 }} />
                </div>
            </div>
        </section>
    );
}

function Realisations() {
    const { t } = useTranslation(['pages', 'common']);

    return (
        <section className="w-full bg-blue overflow-hidden home-realisations" data-navbar-theme="blue">
            <div className="flex gap-32 justify-center items-center home-realisations-layout">
                <div className="text-right w-50 px-32 home-realisations-info">
                    <p className="text-title2 fustat text-semibold t-white mb-32" data-aos="fade-up">{t('pages:home.experienceTitle')}</p>
                    <p className="text-subtitle2 text-light outfit t-white opacity-50 mb-32 home-realisations-info__text" data-aos="fade-up" data-aos-delay="150">
                        {t('pages:home.experienceText')}
                    </p>
                    <div data-aos="fade-up" data-aos-delay="300">
                        <a href="/realisations" className="btn-link">{t('common:buttons.seeProjects')}</a>
                    </div>
                </div>
                <div className="w-50 flex justify-center home-realisations-carousel" data-aos="fade-up" data-aos-delay="200">
                    <VerticalCarousel images={REALISATIONS_IMAGES} />
                </div>
            </div>
        </section>
    );
}

function Emplois() {
    const navigate = useNavigate();
    const { t } = useTranslation(['pages', 'common']);

    const valueKeys = ['passionate', 'creative', 'competent'];
    const delays = [0, 150, 300];

    return (
        <section className="w-full home-emplois" data-navbar-theme="light">
            <div className="flex flex-column justify-center items-center text-center home-emplois__heading">
                <p className="text-title text-semibold fustat text-center mb-16" style={{ maxWidth: '800px'}} data-aos="fade-up">{t('pages:home.careersTitle')}</p>
                <p className="text-subtitle2 text-light outfit mb-32 t-graymain home-emplois__intro" style={{ maxWidth: '800px'}} data-aos="fade-up" data-aos-delay="150">{t('pages:home.careersIntro')}</p>
            </div>
            <div className="flex justify-center gap-32 home-emplois-values">
                {valueKeys.map((key, index) => (
                    <Fragment key={key}>
                        {index > 0 && <hr className="home-emplois-hr" />}
                        {index > 0 && <div className="border-left home-emplois-separator" />}
                        <div className="text-center home-emplois-value" data-aos="fade-up" data-aos-delay={delays[index]}>
                            <p className="text-title3 outfit text-regular mb-32">{t(`pages:home.values.${key}.title`)}</p>
                            <p className="text-subtitle2 text-light outfit mb-32 t-graylight home-emplois-value__text">{t(`pages:home.values.${key}.text`)}</p>
                        </div>
                    </Fragment>
                ))}
            </div>
            <div className="flex justify-center home-emplois__cta" data-aos="fade-up" data-aos-delay="450">
                <button type="button" onClick={() => navigate('/carriere')} className="btn-blue flex items-center justify-center gap-16 home-emplois__button"><span>{t('common:buttons.seeJobs')}</span> <FontAwesomeIcon icon={faAngleRight} /></button>
            </div>
        </section>
    );
}

export default function Home() {
    useAos();
    const { t } = useTranslation('pages');

    return (
        <>
            <PageMeta
                title={t('meta.home.title')}
                description={t('meta.home.description')}
                path="/"
            />
            <Navbar />
            <LocalBusinessSchema />
            <div className="home-page">
                <Hero />
                <Services />
                <Realisations />
                <Emplois />
                <Footer />
            </div>
        </>
    );
}
